import { useState } from 'react'
import { X, Loader2, Building2, Search, CheckCircle, AlertCircle } from 'lucide-react'
import clientesService from '@/services/clientesService'
import toast from 'react-hot-toast'

interface NovoClienteModalProps {
  onClose: () => void
  onSuccess: () => void
}

interface DadosCNPJ {
  nome: string
  fantasia: string
  situacao: string
  email: string
  telefone: string
  uf: string
  municipio: string
}

export default function NovoClienteModal({ onClose, onSuccess }: NovoClienteModalProps) {
  const [loading, setLoading] = useState(false)
  const [consultandoCNPJ, setConsultandoCNPJ] = useState(false)
  const [cnpjConsultado, setCnpjConsultado] = useState(false)
  const [situacaoCNPJ, setSituacaoCNPJ] = useState<string | null>(null)
  
  const [formData, setFormData] = useState({
    cnpj: '',
    razao_social: '',
    nome_fantasia: '',
    email_principal: '',
    telefone: '',
  })

  // Formatador de CNPJ
  const formatarCNPJ = (value: string) => {
    const numeros = value.replace(/\D/g, '').slice(0, 14)
    return numeros
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
  }

  // Limpar CNPJ
  const limparCNPJ = (cnpj: string) => cnpj.replace(/\D/g, '')

  // Consultar CNPJ na ReceitaWS
  const consultarCNPJ = async () => {
    const cnpjLimpo = limparCNPJ(formData.cnpj)
    
    if (cnpjLimpo.length !== 14) {
      toast.error('Digite um CNPJ válido com 14 dígitos')
      return
    }

    setConsultandoCNPJ(true)
    setCnpjConsultado(false)
    setSituacaoCNPJ(null)

    try {
      // ReceitaWS - API gratuita
      const response = await fetch(`https://receitaws.com.br/v1/cnpj/${cnpjLimpo}`, {
        headers: {
          'Accept': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error('Erro na consulta')
      }

      const dados: DadosCNPJ = await response.json()

      if (dados.situacao === 'ERROR' || !dados.nome) {
        toast.error('CNPJ não encontrado na Receita Federal')
        return
      }

      // Preencher formulário com dados da Receita
      setFormData(prev => ({
        ...prev,
        razao_social: dados.nome || prev.razao_social,
        nome_fantasia: dados.fantasia || '',
        email_principal: dados.email || '',
        telefone: dados.telefone ? dados.telefone.replace(/[^\d]/g, '').slice(0, 11) : '',
      }))

      setSituacaoCNPJ(dados.situacao)
      setCnpjConsultado(true)
      
      if (dados.situacao === 'ATIVA') {
        toast.success('CNPJ encontrado! Dados preenchidos automaticamente.')
      } else {
        toast.error(`Atenção: Situação cadastral ${dados.situacao}`)
      }

    } catch (error) {
      console.error('Erro ao consultar CNPJ:', error)
      toast.error('Não foi possível consultar o CNPJ. Preencha manualmente.')
    } finally {
      setConsultandoCNPJ(false)
    }
  }

  // Handler de mudança
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (name === 'cnpj') {
      setFormData((prev) => ({ ...prev, cnpj: formatarCNPJ(value) }))
      // Reset status quando digita novo CNPJ
      if (cnpjConsultado) {
        setCnpjConsultado(false)
        setSituacaoCNPJ(null)
      }
    } else if (name === 'telefone') {
      // Formatar telefone
      const numeros = value.replace(/\D/g, '').slice(0, 11)
      let formatted = numeros
      if (numeros.length > 2) {
        formatted = `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`
      }
      if (numeros.length > 7) {
        formatted = `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`
      }
      setFormData((prev) => ({ ...prev, telefone: formatted }))
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
  }

  // Handler de submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validação
    const cnpjLimpo = limparCNPJ(formData.cnpj)
    if (cnpjLimpo.length !== 14) {
      toast.error('CNPJ inválido')
      return
    }

    if (!formData.razao_social.trim()) {
      toast.error('Razão social é obrigatória')
      return
    }

    setLoading(true)
    try {
      await clientesService.criar({
        cnpj: cnpjLimpo,
        razao_social: formData.razao_social.trim(),
        nome_fantasia: formData.nome_fantasia.trim() || undefined,
        email_principal: formData.email_principal.trim() || undefined,
        telefone: formData.telefone.replace(/\D/g, '') || undefined,
      })

      toast.success('Cliente criado com sucesso!')
      onSuccess()
    } catch (error: any) {
      console.error('Erro ao criar cliente:', error)
      const message = error.response?.data?.error || 'Erro ao criar cliente'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div
          className="px-6 py-4 flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg, #0F1B3D 0%, #1a2d5a 100%)' }}
        >
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-[#FFB800]" />
            <h2 className="text-lg font-semibold text-white">Novo Cliente</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* CNPJ com botão de consulta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              CNPJ <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                name="cnpj"
                value={formData.cnpj}
                onChange={handleChange}
                placeholder="00.000.000/0000-00"
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F1B3D] focus:border-[#0F1B3D]"
                disabled={loading || consultandoCNPJ}
              />
              <button
                type="button"
                onClick={consultarCNPJ}
                disabled={loading || consultandoCNPJ || limparCNPJ(formData.cnpj).length !== 14}
                className="px-4 py-2.5 bg-[#0F1B3D] text-white rounded-lg hover:bg-[#1a2d5a] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                title="Consultar CNPJ na Receita Federal"
              >
                {consultandoCNPJ ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </button>
            </div>
            {/* Status da consulta */}
            {cnpjConsultado && situacaoCNPJ && (
              <div className={`flex items-center gap-1.5 mt-1.5 text-xs ${
                situacaoCNPJ === 'ATIVA' ? 'text-green-600' : 'text-orange-600'
              }`}>
                {situacaoCNPJ === 'ATIVA' ? (
                  <CheckCircle className="w-3.5 h-3.5" />
                ) : (
                  <AlertCircle className="w-3.5 h-3.5" />
                )}
                Situação: {situacaoCNPJ}
              </div>
            )}
          </div>

          {/* Razão Social */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Razão Social <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="razao_social"
              value={formData.razao_social}
              onChange={handleChange}
              placeholder="Nome da empresa"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F1B3D] focus:border-[#0F1B3D]"
              disabled={loading}
            />
          </div>

          {/* Nome Fantasia */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Nome Fantasia
            </label>
            <input
              type="text"
              name="nome_fantasia"
              value={formData.nome_fantasia}
              onChange={handleChange}
              placeholder="Nome fantasia (opcional)"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F1B3D] focus:border-[#0F1B3D]"
              disabled={loading}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Email Principal
            </label>
            <input
              type="email"
              name="email_principal"
              value={formData.email_principal}
              onChange={handleChange}
              placeholder="contato@empresa.com"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F1B3D] focus:border-[#0F1B3D]"
              disabled={loading}
            />
          </div>

          {/* Telefone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Telefone
            </label>
            <input
              type="tel"
              name="telefone"
              value={formData.telefone}
              onChange={handleChange}
              placeholder="(00) 00000-0000"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F1B3D] focus:border-[#0F1B3D]"
              disabled={loading}
            />
          </div>

          {/* Botões */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 text-sm font-semibold rounded-lg text-[#0F1B3D] transition-all hover:brightness-110 disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: '#FFB800' }}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

